import { Routes } from '@angular/router';
import {
  hasFeatureFlag,
  hasSolution,
  PackageFeature,
  packageIncludes,
  redirectToSolutionLanding,
  redirectToSystem,
} from '@twaice-fe/frontend/shared/services';
import { FeatureNotIncludedComponent } from '@twaice-fe/frontend/shared/ui-components';
import { FeatureFlagsEnum, Solution } from '@twaice-fe/shared/models';
import { AnalyticsUpsellComponent } from './views/analytics-upsell/analytics-upsell.component';
import { ProfessionalServicesComponent } from './views/professional-services/professional-services.component';
import { SolutionComponent } from './views/solution/solution.component';
import { SubPageComponent } from './views/sub-page/sub-page.component';

export const solutionRoutes: Routes = [
  {
    path: '',
    canActivate: [redirectToSolutionLanding],
    component: SolutionComponent,
  },
  {
    path: 'solutions',
    pathMatch: 'full',
    redirectTo: '',
  },
  {
    path: 'energy',
    component: SolutionComponent,
    children: [
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full',
      },
      {
        path: 'overview',
        component: SubPageComponent,
        children: [
          {
            path: '',
            loadChildren: () =>
              import('@twaice-fe/frontend/features/energy-analytics/portfolio-overview').then(
                (m) => m['EnergyPortfolioOverviewModule']
              ),
          },
        ],
      },
      // the Degradation page
      {
        path: 'health',
        canMatch: [packageIncludes(PackageFeature.Degradation)],
        component: SubPageComponent,
        canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
        children: [
          {
            path: '',
            loadChildren: () =>
              import('@twaice-fe/frontend/features/energy-analytics/health').then((m) => m['EnergyHealthModule']),
          },
        ],
      },
      {
        path: 'health',
        data: { feature: PackageFeature.Degradation },
        children: [{ path: '**', component: FeatureNotIncludedComponent }],
      },
    ],
  },
  {
    path: 'warranty-tracker',
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/warranty-tracker').then((m) => m['WarrantyTrackerModule']),
      },
    ],
  },
  {
    path: 'data-explorer',
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/data-explorer').then((m) => m['routes']),
      },
    ],
  },
  {
    path: 'reports',
    component: SubPageComponent,
    canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/reports').then((m) => m['ReportsModule']),
      },
    ],
  },
  {
    path: 'storage-overview',
    component: SubPageComponent,
    canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/storage-overview').then((m) => m['routes']),
      },
    ],
  },
  {
    path: 'storage-hierarchy',
    component: SubPageComponent,
    canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/storage-hierarchy').then((m) => m['routes']),
      },
    ],
  },
  // legacy professional services reports route
  // deprecated as of 04.03.25
  {
    path: 'reporting',
    redirectTo: 'professional-services',
  },
  {
    path: 'reporting/:subRoute',
    redirectTo: 'professional-services',
  },
  {
    path: 'professional-services',
    component: ProfessionalServicesComponent,
  },
  {
    path: 'safety/incidents',
    redirectTo: '/alerts',
    pathMatch: 'full',
  },
  {
    path: 'system-monitoring',
    data: { redirectTo: 'alerts' },
    redirectTo: (route) => route.data['redirectTo'],
    pathMatch: 'prefix',
  },
  {
    path: 'alerts',
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/alerts').then((m) => m['AlertsModule']),
      },
    ],
  },
  {
    path: 'bms-alerts',
    canMatch: [packageIncludes(PackageFeature.ErrorCodes)],
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/alerts').then((m) => m['BMSAlertsModule']),
      },
    ],
  },
  {
    path: 'bms-alerts',
    data: { feature: PackageFeature.ErrorCodes },
    children: [{ path: '**', component: FeatureNotIncludedComponent }],
  },
  {
    path: 'events',
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.ENERGY), hasFeatureFlag({ featureFlag: FeatureFlagsEnum.ENABLE_INSIGHTS_AND_ACTIONS })],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/insights-and-actions').then((m) => m['routes']),
      },
    ],
  },
  {
    path: 'performance-manager',
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/performance-manager').then((m) => m['PerformanceManagerModule']),
      },
    ],
  },
  // the Anomalies page
  {
    path: 'safety',
    canMatch: [packageIncludes(PackageFeature.Anomalies)],
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.ENERGY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@twaice-fe/frontend/features/safety-recommendation').then((m) => m['SafetyRecommendationModule']),
      },
    ],
  },
  {
    path: 'safety',
    data: { feature: PackageFeature.Anomalies },
    children: [{ path: '**', component: FeatureNotIncludedComponent }],
  },
  {
    path: 'penalty-risk',
    canMatch: [packageIncludes(PackageFeature.PenaltyRisk)],
    component: SolutionComponent,
    canActivate: [
      hasSolution(Solution.ENERGY),
      redirectToSystem,
      hasFeatureFlag({ featureFlag: FeatureFlagsEnum.HIDE_PENALTY_RISK, isNegated: true }),
    ],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/penalty-risk').then((m) => m['PenaltyRiskModule']),
      },
    ],
  },
  {
    path: 'penalty-risk',
    data: { feature: PackageFeature.PenaltyRisk },
    children: [{ path: '**', component: FeatureNotIncludedComponent }],
  },
  {
    path: 'mobility',
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.MOBILITY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@twaice-fe/frontend/features/fleet-prediction').then((m) => m['FleetPredictionFeatureModule']),
      },
    ],
  },
  {
    path: 'mobility-explore',
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.MOBILITY), redirectToSystem],
    children: [
      {
        path: '',
        loadChildren: () => import('@twaice-fe/frontend/features/mobility-explore').then((m) => m.routes),
      },
    ],
  },
  {
    path: 'model-library',
    component: SolutionComponent,
    canActivate: [hasSolution(Solution.MODEL_LIBRARY)],
    children: [
      {
        path: '',
        redirectTo: 'library',
        pathMatch: 'full',
      },
      {
        path: 'library',
        children: [
          {
            path: '',
            loadChildren: () => import('@twaice-fe/frontend/features/model-library').then((m) => m['ModelLibraryModule']),
          },
        ],
      },
    ],
  },
  // generic analytics upsell page
  {
    path: 'analytics',
    canActivate: [hasSolution(Solution.MODEL_LIBRARY)],
    component: AnalyticsUpsellComponent,
  },
  // generic analytics upsell page
  {
    path: 'analytics',
    canActivate: [hasSolution(Solution.MODEL_LIBRARY)],
    component: AnalyticsUpsellComponent,
  },
];
